import EmptyPageContent from '@app/ccf/components/empty-page-content';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { FEATURES } from '@app/utils/constants';
import { useAccount } from '@shared/api';
import { useGetPurchasedGoodsInsights } from '@shared/api/hooks/purchased-goods/insights';
import { useGetTimePeriods } from '@shared/api/hooks/reports/periods';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import Loader from '@shared/components/loader';
import { cn } from '@shared/lib/utils';
import { Icons } from '@shared/components/content/icons';
import { ApiValidationError } from '@app/api/errors';
import type { AxiosError } from 'axios';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchasedGoodsRoutes } from '../../purchased-goods-routes';
import { InsightsBreakdownTable } from '../components/insights-breakdown-table';
import { InsightsExplorePills } from '../components/insights-explore-pills';
import { InsightsFilters } from '../components/insights-filters';
import { InsightsGroupedCharts } from '../components/insights-grouped-charts';
import { DashboardLastUpdated } from '@shared/components/dashboard/last-updated';
import { InsightsKpiRow } from '../components/insights-kpi-row';
import { InsightsOverTimeSection } from '../components/insights-over-time-section';
import { InsightsPeriodPicker } from '../components/insights-period-picker';
import {
  useInsightsApiParams,
  useInsightsChartType,
  useInsightsDimension,
  useInsightsFilters,
  useInsightsPeriodRange,
  useInsightsTableState,
  useInsightsTaxonomy,
} from '../hooks/use-insights-filters';
import { SPEND_CHART_TYPES } from '../components/insights-grouped-charts';

/** Sort keys of the spend columns, cleared from the URL for locked accounts. */
const SPEND_SORT_KEYS = ['spend', 'spendPerKg', 'emissionsPerSpendUnit'];

/**
 * The endpoint rejects organisations that have never had insights generated
 * (nothing in the aggregation view) with a 400 carrying this message — a
 * distinct onboarding state, not a failure.
 *
 * The global response interceptor (src/api/config.ts) replaces 400s with an
 * ApiValidationError holding the body on `.data`, so check that shape first
 * and fall back to a raw AxiosError response for completeness.
 */
function isNoPurchaseData(error: unknown): boolean {
  const payload =
    (error as ApiValidationError | null)?.data ??
    (error as AxiosError | null)?.response?.data;
  if (!payload) return false;
  return JSON.stringify(payload).toLowerCase().includes('no purchase data');
}

/**
 * How each grouping is named on the page. The three taxonomy tiers all read
 * "category": subcategory and base product are our own vocabulary, and which
 * tier is in view is already clear from the filter pill's breadcrumb.
 */
const DIMENSION_LABELS = {
  site: 'Site',
  supplier: 'Supplier',
  productCategory: 'Category',
  productSubCategory: 'Category',
  productBase: 'Category',
  item: 'Item',
} as const;

const DIMENSION_PLURALS = {
  site: 'sites',
  supplier: 'suppliers',
  productCategory: 'categories',
  productSubCategory: 'categories',
  productBase: 'categories',
  item: 'items',
} as const;

/**
 * Dims a data section while a new scope is being fetched, keeping the stale
 * content visible for continuity. Interactions are blocked on the stale data;
 * the page's controls live outside these wrappers and stay usable.
 */
function UpdatingSection({
  updating,
  spinner = false,
  children,
}: {
  updating: boolean;
  /** Show a centred spinner on top of the dimmed content. */
  spinner?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <div
        className={cn(
          'transition-opacity',
          updating && 'pointer-events-none opacity-50'
        )}
        aria-busy={updating}
      >
        {children}
      </div>
      {updating && spinner && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader size="sm" />
        </div>
      )}
    </div>
  );
}

/** Inline failure state — the header and controls stay usable for a retry or a scope change. */
function InsightsError({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyPageContent
      title="We couldn't load your insights"
      description="There was an error loading your insights, please try again or get in touch if the issue persists"
      primaryAction={onRetry}
      primaryActionText="Refresh"
      primaryActionIcon={<Icons.refreshCcw />}
    />
  );
}

/**
 * Purchasing Insights — the exploration surface for the carbon and cost of a
 * group's purchased goods: headline metrics, filters and groupings, comparison
 * charts, and a breakdown table for the active grouping.
 */
export default function PurchasingInsightsPage() {
  const navigate = useNavigate();
  const { data: account } = useAccount(true);
  const { data: orgPeriods } = useGetTimePeriods();
  const params = useInsightsApiParams();
  // An org without time periods has no purchase data at all — the endpoint
  // rejects the request, so don't make it; the page renders a blank state.
  const hasPeriods = !orgPeriods || orgPeriods.length > 0;
  const { data, isLoading, isFetching, isError, error, refetch, status } =
    useGetPurchasedGoodsInsights(params, {
      enabled: hasPeriods && !!orgPeriods,
    });
  const dimension = useInsightsDimension();
  const [filters, setFilters] = useInsightsFilters();
  const { values: taxonomy, clear: clearTaxonomy } = useInsightsTaxonomy();
  const [range, setRange] = useInsightsPeriodRange();
  const [tableState, setTableState] = useInsightsTableState();
  const [chartType, setChartType] = useInsightsChartType();

  // A scope change refetching behind kept-previous data (not the first load).
  const isUpdating = isFetching && !isLoading;
  // Table row under the cursor — its point is highlighted on the scatter chart.
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // The filter/period scope the displayed data was loaded for. Explore, page
  // and sort changes refetch too, but they can't change the headline metrics,
  // so the hero row only dims when the scope itself is changing.
  const scopeKey = JSON.stringify([filters, taxonomy, range]);
  // Drilling swaps the rows under a stationary cursor without a mouseleave —
  // drop the hover rather than highlight against the previous dimension.
  useEffect(() => setHoveredRowId(null), [dimension, scopeKey]);
  const loadedScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (!isFetching) loadedScopeKey.current = scopeKey;
  }, [isFetching, scopeKey]);
  const isScopeUpdating = isUpdating && scopeKey !== loadedScopeKey.current;

  const hasCostFeature = !!account?.features[FEATURES.cost];
  const hasSpendData = data?.spend != null;

  // A shared or stale URL can carry a spend chart or sort into a locked
  // account — reset those params to the default emissions view. Only once
  // the account has actually loaded: before that, hasCostFeature is false
  // for everyone and this would wipe entitled users' params.
  useEffect(() => {
    if (!account || hasCostFeature) return;
    if (SPEND_CHART_TYPES.includes(chartType)) setChartType(null);
    if (
      tableState.sort &&
      SPEND_SORT_KEYS.includes(tableState.sort.replace(/^-/, ''))
    ) {
      setTableState({ sort: null });
    }
  }, [
    account,
    hasCostFeature,
    chartType,
    setChartType,
    tableState.sort,
    setTableState,
  ]);
  const isFiltered =
    filters.site.length > 0 ||
    filters.supplier.length > 0 ||
    filters.item.length > 0 ||
    !!taxonomy.productCategory ||
    !!taxonomy.productSubCategory ||
    !!taxonomy.productBase;
  const hasExplicitPeriod = !!(range.periodStart && range.periodEnd);

  // The org has insights, but this filter/period scope matches nothing.
  const isScopedToNothing =
    !!data &&
    !isLoading &&
    (data.purchases ?? 0) === 0 &&
    (isFiltered || hasExplicitPeriod);
  // The org has never had insights generated at all.
  const hasNoInsights = isError && isNoPurchaseData(error);
  // There are purchases in scope, but none carry the grouping dimension —
  // e.g. exploring by supplier when imports have no supplier information.
  // Judged only on settled data: mid-fetch, kept-previous data from another
  // grouping has no rows for this one and would misread as empty.
  const isDimensionEmpty =
    dimension !== 'overall' &&
    !!data &&
    !isFetching &&
    !isScopedToNothing &&
    (data.groupByItems?.count ?? 0) === 0;

  const resetScope = () => {
    setFilters({ site: null, supplier: null, item: null });
    clearTaxonomy();
    setRange({ periodStart: null, periodEnd: null });
    setTableState({ page: null, sort: null });
  };

  const grouped = dimension !== 'overall';
  const dimensionLabel = grouped ? DIMENSION_LABELS[dimension] : null;
  const pageRows = data?.groupByItems?.results ?? [];

  return (
    <Page
      name="Purchasing insights"
      page="page"
      // Errors render inline below so the filters that caused them stay
      // reachable; only the initial load uses the page-level loader.
      status={hasPeriods && !isError ? status : undefined}
    >
      <PageHeader
        name="Purchasing insights"
        title="Purchasing insights"
        breadcrumbs={[
          {
            label: 'Purchased goods',
            url: purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK,
          },
        ]}
        toolbar={
          hasPeriods ? (
            <div className="flex items-center gap-6">
              <DashboardLastUpdated
                lastRefreshedAt={data?.lastRefreshedAt}
                hoverText="Insights data is updated hourly or after every file import."
              />
              <InsightsPeriodPicker
                defaultStart={data?.timePeriodStart}
                defaultEnd={data?.timePeriodEnd}
              />
            </div>
          ) : undefined
        }
      />
      {!hasPeriods && (
        <EmptyPageContent
          title="No purchase data"
          description="Import your purchase data to start exploring the carbon and cost of everything you buy."
          primaryAction={() =>
            navigate(purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT)
          }
          primaryActionText="Import data"
        />
      )}
      {hasPeriods && hasNoInsights && (
        <EmptyPageContent
          title="No insights available"
          description="Your purchasing insights haven't been generated yet. They'll appear here once your purchase data has been processed."
          primaryAction={() => refetch()}
          primaryActionText="Refresh"
          primaryActionIcon={<Icons.refreshCcw />}
        />
      )}
      {hasPeriods && !hasNoInsights && (
        <div className="flex flex-col gap-6">
          {!isError && !isScopedToNothing && (
            <UpdatingSection updating={isScopeUpdating}>
              <InsightsKpiRow
                data={data}
                currency={account?.defaultCurrency}
                hasCostFeature={hasCostFeature}
                isFiltered={isFiltered}
                isLoading={isLoading}
              />
            </UpdatingSection>
          )}
          <div className="flex flex-col gap-3">
            <InsightsExplorePills />
            <InsightsFilters available={data?.availableFilters} />
          </div>
          {isError ? (
            <InsightsError onRetry={() => refetch()} />
          ) : isScopedToNothing ? (
            <EmptyPageContent
              title="No matching purchase data"
              description="There is no purchase data for the selected filters and time period"
              primaryAction={resetScope}
              primaryActionText="Reset filters"
              primaryActionIcon={<Icons.x />}
            />
          ) : grouped && isFetching && !data?.groupByItems ? (
            // Switching from overall to a grouping: the kept-previous data has
            // no grouped rows at all, so there's nothing sensible to dim.
            <div className="flex items-center justify-center py-24">
              <Loader size="sm" />
            </div>
          ) : isDimensionEmpty && dimensionLabel ? (
            <EmptyPageContent
              title={`No ${dimensionLabel.toLowerCase()} data`}
              description={`The selected purchase data is not linked to ${DIMENSION_PLURALS[dimension]}, so we cannot show a breakdown`}
            />
          ) : (
            <UpdatingSection updating={isUpdating} spinner>
              {grouped && dimensionLabel ? (
                <div className="flex flex-col gap-6">
                  <InsightsGroupedCharts
                    rows={pageRows}
                    dimensionLabel={dimensionLabel}
                    currency={account?.defaultCurrency}
                    hasSpendData={hasSpendData}
                    hasCostFeature={hasCostFeature}
                    highlightedId={hoveredRowId}
                  />
                  <DashboardSection
                    title={`${dimensionLabel} comparison`}
                    description={`Compare emissions and spend across ${DIMENSION_PLURALS[dimension]}. Filter, sort, and change page to explore your data, click an item to drill down.`}
                  >
                    <InsightsBreakdownTable
                      page={data?.groupByItems}
                      dimension={dimension}
                      dimensionLabel={dimensionLabel}
                      hasCostFeature={hasCostFeature}
                      currency={account?.defaultCurrency}
                      isLoading={isLoading}
                      onRowHover={setHoveredRowId}
                    />
                  </DashboardSection>
                </div>
              ) : (
                <InsightsOverTimeSection
                  data={data}
                  currency={account?.defaultCurrency}
                  hasSpendData={hasSpendData}
                  hasCostFeature={hasCostFeature}
                />
              )}
            </UpdatingSection>
          )}
        </div>
      )}
    </Page>
  );
}
